import React from 'react'
import PropTypes from 'prop-types'
import { isBrowser } from 'react-device-detect'
import { TweenMax } from 'gsap'
import globals from '../globals'
import DataSource from '../DataSource'
import AjaxLoader from '../components/AjaxLoader'
import FormSuccess from '../components/FormSuccess'
import Error from '../components/Error'

/*
Form - Higher Order Component
This HOC should work as a wrapper around most (if not all) forms in this app. It handles the fairly similar
loading/validating/submitting functionality which is replicated for all forms.

See existing forms for example usage with existing form components but please ensure you follow the following schema
when creating components:

// WRAPPER COMPONENT
<MyComponent>
	<MyForm fields={ Input values object here if default values required from API ie this.state.fields } />
</MyComponent>

// CHILD FORM COMPONENT
<MyForm>
	...initialisation code here

	<InputRow
		label="Delivery recipient"
		name="name"
		value={props.values.name}
		errors={props.errors}
		handleChange={props.handleInputChange}
	/>

	...submit btn etc here
</MyForm>

options: {
    postUrl (string),
    success (string),
    dontScrollToNav (bool),
    showHideIcon: (bool),
}
*/

const FormHOC = (WrappedComponent, formValues, options) => {
    return class extends React.Component {
        constructor (props) {
            super(props)

            this.state = {
			    updateKey: 0,
                hasGenericError: false,
                isLoading: false,
                isFinished: false,
                errors: {},
                values: { ...formValues },
                successMsg: options.success
            }
        }

        componentDidMount () {
            if (this.props.fields && Object.keys(this.props.fields).length > 0) {
                this.setDynamicValues(this.props.fields)
            }
        }

        componentDidUpdate (prevProps) {
            if (this.props.fields && prevProps.fields !== this.props.fields) {
                this.setDynamicValues(this.props.fields)
            }
        }

        setDynamicValues (values, onComplete) {
            const updateErrors = () => {
                Object.keys(values).forEach(key => {
                    if (this.state.errors[key] && this.state.values[key] !== '') {
                        this.updateError(key)
                    }
                })
            }
            this.setState({
                values: { ...this.state.values, ...values }
            }, () => {
                updateErrors()
                return onComplete ? onComplete() : null
            })
        }

        handleSubmit (event = null, data = null, newPostUrl = null) {
		    if (event) {
                event.preventDefault()
            }
            this.reset()
            this.showLoader()
            this.scrollToNav()

            const submissionData = data || this.state.values
            const urlToUse = newPostUrl || options.postUrl

            setTimeout(() => {
                DataSource.postData(
                    urlToUse,
                    submissionData,
                    this.handleSuccess.bind(this),
                    this.handleFailure.bind(this)
                )
            }, 1000)
        }

        handleKeyDown (event) {
            if (event.keyCode === 13) {
                event.preventDefault()
                this.handleSubmit(event)
            }
        }

        handleSuccess (response) {
            this.hideLoader()
            this.setState({ isFinished: true })
            if (this.props.setSuccessMsg) {
			    this.setState({
                    successMsg: this.props.setSuccessMsg(response, this.state.values)
                })
            }
            if (this.props.handleSuccess) {
                this.props.handleSuccess(response, this.state.values)
            }
        }

        handleFailure (response) {
            if (response.response && response.response.status === 422) {
                this.setState({ errors: response.response.data.errors })
            } else if (response.response && response.response.status === 401) {
                let message = 'Your username and/or password is incorrect'
                if (response.response.data.error && response.response.data.error === 'business_user') {
                    message = response.response.data.error_description
                }
                this.setState({ errors: { password: [message] } })
            } else {
                this.setState({ hasGenericError: true })
                globals.handleError(response.message, response)
            }

            this.hideLoader()

            if (this.props.handleFailure) {
                this.props.handleFailure(response, this.state.values)
            }
        }

        handleInputChange (event) {
            const target = event.target
            this.setState({
                values: { ...this.state.values, [target.name]: target.value }
            }, () => this.updateError(target.name))
        }

        showLoader () {
            this.setState({ isLoading: true })
        }

        hideLoader () {
            this.setState({ isLoading: false })
        }

        reset () {
            this.setState({
                hasGenericError: false,
                errors: {}
            })
        }

        hasErrors () {
            return Object.keys(this.state.errors).length > 0
        }

        updateError (errorKey) {
            // If the user starts typing, remove the error state from that element
            if (this.state.errors[errorKey]) {
                const errorKeys = Object.keys(this.state.errors).filter(error => error !== errorKey)
                const errors = {}
                errorKeys.forEach(key => {
                    errors[key] = this.state.errors[key]
                })
                this.setState({ errors: errors })
            }
        }

        scrollToNav () {
            if (document.getElementById('sub-navigation') && !options.dontScrollToNav) {
                if (isBrowser) {
                    TweenMax.to(window, 0.5, { scrollTo: { y: document.getElementById('sub-navigation'), offsetY: 60 } })
                } else {
                    if (document.getElementById('sub-navigation-mobile')) {
                        TweenMax.to(window, 0.5, { scrollTo: { y: document.getElementById('sub-navigation-mobile'), offsetY: 40 } })
                    } else {
                        TweenMax.to(window, 0.5, { scrollTo: { y: document.getElementById('sub-navigation'), offsetY: 75 } })
                    }
                }
            }
        }

        hideSuccess () {
            this.setState({ isFinished: false })
        }

        render () {
            return (
                this.state.isLoading
                    ? <AjaxLoader />
                    : this.state.isFinished
                        ? <FormSuccess message={this.state.successMsg} hideSuccess={options.showHideIcon ? this.hideSuccess.bind(this) : null} />
                        : <WrappedComponent
                                key={this.state.updateKey}
                                values={this.state.values}
                                errors={this.state.errors}
                                data={this.state}
                                setDynamicValues={this.setDynamicValues.bind(this)}
                                handleSubmit={this.handleSubmit.bind(this)}
                                handleKeyDown={this.handleKeyDown.bind(this)}
                                handleInputChange={this.handleInputChange.bind(this)}
                                {...this.props}
                        >
                            {this.hasErrors() ? <Error errors={this.state.errors} /> : ''}
                            {this.state.hasGenericError ? <div className='row-error'>Oops! Something went wrong. Please try again to see if the issue persists.</div> : ''}
                          </WrappedComponent>
            )
        }
    }
}

FormHOC.propTypes = {
    handleSuccess: PropTypes.func,
    setSuccessMsg: PropTypes.func,
    handleFailure: PropTypes.func
}

export default FormHOC



// WEBPACK FOOTER //
// ./resources/js/forms/FormHOC.js